import React, { useEffect, useCallback, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useFormik } from 'formik'
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { createCustomHandleApostropheChange, removeTrailingSpacesAndDashes } from '../../../Components/CleanInputs';
import classNames from "classnames";
import { useTranslation, Trans } from "react-i18next";
import CustomToast from '../../../Components/CustomToast'
import { useSelector, useDispatch } from "react-redux";
import "../../../Styles/components/tags.scss";

export const CreateTagGroup = ({ visible, hideTagGroupPopup, businessId, getSummaryCustomers, pageOptions, createTagGroupDisplay, onTagsChanged }) => {

    const { t } = useTranslation("common");
    const [errorDisplay, setErrorDisplay] = useState({});
    const [toastTrigger, setToastTrigger] = useState(false);
    const [clickSave, setClickSave] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [submit, setSubmit] = useState(0);

    const [tagToast, setTagToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });

    const dispatch = useDispatch()
    const createTagGroup = useCallback((tagGroupContent) => dispatch(BusinessCustomersActions.createTagGroupRequest(tagGroupContent)), [dispatch]);
    const getTagGroups = useCallback(
        (id) => dispatch(BusinessCustomersActions.tagGroupRequest(id, pageOptions)),
        [dispatch, pageOptions]
    );
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);

    //Redux State from Store
    const { createdTagGroup, creatingTagGroup, createTagGroupError } = useSelector((state) => state.businessCustomers);
    const defMembership = useSelector((state) => state.defMembership.defMembership);

    const errorMessage = createTagGroupError?.detail?.split('"')[1] ? createTagGroupError?.detail?.split('"')[1] : createTagGroupError?.detail;
    const match = errorMessage?.match(/\[(.*?)\]/);
    const existingTagGroupName = match ? match[1] : null;

    useEffect(() => {
        if (!creatingTagGroup && clickSave && createdTagGroup) {
            showTagToast("success", t("tags.success"), t("tags.tag_success_group"))
        }
        if (!creatingTagGroup && clickSave && !createdTagGroup) {
            showTagToast("error", t("tags.error"), errorMessage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingTagGroup, clickSave, createdTagGroup]);

    const showTagToast = (severity, summary, detail) => {
        setTagToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setToastTrigger(true);
    }

    const formik = useFormik({
        initialValues: {
            name: ''
        },
        validate: values => {
            const errors = {};
            if (!values.name.trim()) {
                errors.name = t("tags.tag_name_required");
            }
            return errors;
        },

        onSubmit: (data) => {
            setClickSave(true);
            const tagGroupContent = {
                name: removeTrailingSpacesAndDashes(data.name) || null,
                business: { id: businessId },
            };
            createTagGroup(tagGroupContent);
        },
    }
    );

    useEffect(() => {
        if (!createTagGroupDisplay) {
            formik.resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createTagGroupDisplay]);

    const { handleBlur, values } = formik;

    const commonParams = [formik, setErrorDisplay, t];
    const customHandleChange = createCustomHandleApostropheChange(...commonParams);

    const isFormFieldValid = (name) =>
        !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && (
                <small className="character_errors">{formik.errors[name]}</small>
            )
        );
    };

    return (
        <div>
            <Dialog
                visible={visible}
                onHide={() => {
                    formik.resetForm();
                    setShowErrorMessage(false);
                    setClickSave(false);
                    setToastTrigger(false);

                    hideTagGroupPopup();
                }}
                header={t("tags.create_tag_group")}
                style={{ width: "350px" }}
            >
                <CustomToast
                    {...tagToast}
                    trigger={toastTrigger}
                    handleActionsAfterToast={() => {
                        setToastTrigger(false);
                        setClickSave(false);
                        hideTagGroupPopup();
                        getTagGroupsGraphed(defMembership.business.id)
                        if (getSummaryCustomers) {
                            getSummaryCustomers(defMembership?.business.id, pageOptions);
                        }
                        getTagGroups(defMembership?.business.id);
                        onTagsChanged?.();
                        formik.resetForm();
                        formik.setFieldValue('name', '');
                        setShowErrorMessage(false);
                        setSubmit(0)
                    }}
                    handleActionsAfterErrorToast={() => {
                        setShowErrorMessage(true);
                        setToastTrigger(false);
                        setClickSave(false);
                    }}
                />

                <form onSubmit={formik.handleSubmit} className="p-fluid p-3" style={{ paddingInline: "4px" }}>
                    <div className="p-field p-mb-3" >
                        <label
                            id='name_label'
                            htmlFor="name"
                            className={classNames("product-form-label", {
                                "": isFormFieldValid("name")
                            })}
                        >
                            {t("tags.create_tag_group_label")}
                        </label>
                        <InputText
                            id="name"
                            name="name"
                            value={values.name}
                            onChange={(e) => {
                                customHandleChange(e);

                                const rawValue = e.target.value;
                                const hasInvalidChars = /[^\p{L}\p{N}\s\-(),'’‘"]/u.test(rawValue);

                                if (hasInvalidChars) {
                                    formik.setFieldError("name", undefined);
                                }
                                formik.setFieldTouched("name", true, false);
                                setShowErrorMessage(false);
                            }}
                            style={{ width: "100%" }}
                            maxLength={25}
                            onBlur={handleBlur}
                        />
                        {errorDisplay.name ? (
                            <p className="character_errors">{errorDisplay.name}</p>
                        ) : (
                            <>
                                {getFormErrorMessage("name")}

                                {createTagGroupDisplay && !formik.errors.name && (
                                    <p className="tag-field-hint">{t("tags.tag_field_hint")}</p>
                                )}
                            </>
                        )}
                        {showErrorMessage ?
                            <div className='mt-1'>
                                <Trans
                                    i18nKey="tags.message_group_name_exists"
                                    values={{
                                        groupName: existingTagGroupName
                                    }}
                                    components={{
                                        1: <span className="tags_error_message" />,
                                        2: <span className="tags_error_message_bold" />,
                                        3: <span className="tags_error_message" />
                                    }}
                                />
                            </div>
                            : null}
                    </div>

                    <div className="p-field p-mb-3 mt-4  gap-3" >
                        <div className="gap-3" style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem", height: "2rem" }}>
                            <Button
                                type="button"
                                label={t('subscriber.cancel')}
                                icon="pi pi-times"
                                onClick={() => {
                                    formik.resetForm();
                                    setShowErrorMessage(false);
                                    setClickSave(false);
                                    setToastTrigger(false);
                                    hideTagGroupPopup();
                                }}
                                id='cancel-tag-button'
                            />
                            <Button
                                type="submit"
                                label={t("products.save_product")}
                                className="p-mt-2 save-btns"
                                onClick={() => {
                                    localStorage.removeItem('toastShown');
                                    setSubmit(submit + 1)
                                }}
                                disabled={creatingTagGroup || !formik.values.name || clickSave}
                                loading={creatingTagGroup} />
                        </div>
                    </div>
                </form>
            </Dialog>
        </div >
    )
}